/**
 * src/gm/index.ts — v2 引擎对外入口（设计稿 §7/§8）。
 *
 * 模块地图：
 *   platform/    契约镜像类型、SaveStorePort 端口、常量、错误、锁、产物区
 *   session/     会话显式化（codec + 持久化）
 *   events/      持久事件流 + 瞬时 live-bus
 *   employees/   api-client / prompt-builder / parser / session 化 runner
 *   sandbox/     冒烟探针台架
 *   settlement/  纯公式 + 有序落盘 + 脏标记幂等
 *   kernel/      状态机 / 门禁表 / 唤醒队列 / GameMaster 门面
 *
 * 偏差登记：设计稿 §7 提到 index 提供 React hooks——React 集成属外壳层（M3），
 * 引擎保持零 React 依赖；§8 接口承诺的 subscribe/getRunState 已全部给出，
 * 外壳用 useSyncExternalStore 包一层即可。已在记忆文件登记。
 */

export { GameMaster } from './kernel/game-master'
export type { GameMasterDeps, ResumeReport, RunStateInfo } from './kernel/game-master'
export { GATE_TABLE, gatesFor } from './kernel/gates'
export type { GateId, ProjectTier } from './kernel/gates'
export type {
  WakeupAction,
  WakeupRequest,
  WakeupSource,
  WakeupState,
} from './kernel/wakeup-queue'

export { ApiClient, STREAM_INTERRUPTED } from './employees/api-client'
export type { ApiClientDeps, ChatMessage, ChatOptions, ChatResult, Usage } from './employees/api-client'

export { GmError, ParseError, translateHttpError } from './platform/errors'
export type { GmErrorCode } from './platform/errors'
export { modelFor, REVIEWER_CHOICE } from './platform/model-map'
export type { ModelChoice } from './platform/model-map'

export { readArtifact, writeArtifact, clearArtifacts, estimateStorageBytes } from './platform/artifacts'
export type { ArtifactKind } from './platform/artifacts'
export type {
  Decision,
  DecisionKind,
  DimensionKey,
  Employee,
  EmployeeRole,
  MarketData,
  Project,
  ProjectStatus,
  Rarity,
  Review,
  SaveBundle,
  SaveData,
  SaveStorePort,
  TokenUsage,
} from './platform/save-types'

export {
  appendEvent,
  narrate,
  replayEvents,
  subscribeEvents,
} from './events/event-log'
export type { GmPhase, RunEvent, RunEventKind } from './events/event-log'
export { subscribeLive } from './events/live-bus'
export type { StreamDelta } from './events/live-bus'

export { loadSession, clearProjectSessions } from './session/session-store'
export type { SessionRecord, StageKind } from './session/session-codec'

export { smokeTest, computeVerdict } from './sandbox/smoke-probe'
export type { SmokeReport } from './sandbox/smoke-probe'

export {
  readPendingSettlement,
  clearPendingSettlement,
  buildReview,
  settle,
} from './settlement/settle'
export type { PendingSettlement, SettleResult } from './settlement/settle'
export {
  calcCost,
  calcExpGained,
  calcRevenue,
  calcWeightedTotal,
  levelFor,
} from './settlement/formulas'

/** 工厂（外壳注入 SaveStorePort 与可选替身；引擎不持有单例——单例策略归外壳） */
import { GameMaster } from './kernel/game-master'
import type { GameMasterDeps } from './kernel/game-master'

export function createGameMaster(deps: GameMasterDeps): GameMaster {
  return new GameMaster(deps)
}
